import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import type { CanvasserPerformanceSummary } from '../../services/reporting';

interface CanvasserPerformanceReportProps {
  canvasserSummaries: CanvasserPerformanceSummary[];
  loading: boolean;
}

const CanvasserPerformanceReport: React.FC<CanvasserPerformanceReportProps> = ({
  canvasserSummaries,
  loading
}) => {
  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Canvasser Performance Rankings</h3>
        {canvasserSummaries.length === 0 && !loading ? (
          <p className="text-gray-500">No canvasser data available for this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canvasser</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Visits</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Surveys</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visits/Hour</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {canvasserSummaries.map((canvasser, index) => (
                  <tr key={canvasser.userId} className={index < 3 ? 'bg-yellow-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      {index + 1}
                      {index === 0 && ' \ud83e\udd47'}
                      {index === 1 && ' \ud83e\udd48'}
                      {index === 2 && ' \ud83e\udd49'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{canvasser.userName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{canvasser.totalVisits}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{canvasser.completedAddresses}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <div className="w-16 h-2 bg-gray-200 rounded mr-2">
                          <div
                            className="h-2 bg-green-500 rounded"
                            style={{ width: `${canvasser.completionRate}%` }}
                          />
                        </div>
                        {canvasser.completionRate}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">{canvasser.surveysCompleted}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{canvasser.avgVisitsPerHour.toFixed(1)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{canvasser.avgTimePerAddress} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Performance Charts */}
      {canvasserSummaries.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Completed Addresses by Canvasser</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={canvasserSummaries.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="userName" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="completedAddresses" fill="#22C55E" name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium mb-4">Completion Rate Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={canvasserSummaries.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="userName" tickFormatter={(val) => val.split(' ')[0]} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="completionRate" fill="#6366F1" name="Completion %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CanvasserPerformanceReport;
